import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useUser } from "@clerk/react";
import { ArrowLeft, Printer, Lock } from "lucide-react";
import { Btn, BtnLink } from "@/components/kit";
import { PLATFORM } from "@/config/platform";
import { useProgress } from "@/modules/premiere-video/store";
import { doneCount, filled } from "@/modules/premiere-video/logic";

const Corner = ({ className }) => <span className={`absolute h-10 w-10 border-gold ${className}`} aria-hidden />;

function NotYet({ done }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-ivory px-5" data-testid="certificate-locked">
      <div className="max-w-md rounded-[24px] border border-line bg-white p-8 text-center">
        <Lock className="mx-auto h-8 w-8 text-gold" strokeWidth={1.5} />
        <h1 className="mt-4 text-2xl font-semibold text-bx">Ton certificat t'attend</h1>
        <p className="mt-3 text-soft">Il se débloque quand les 7 missions de UGC GO sont terminées. Tu en as terminé {done} sur 7.</p>
        <BtnLink to="/parcours" className="mt-6" data-testid="certificate-back-journey">Continuer mon parcours</BtnLink>
      </div>
    </div>
  );
}

export default function Certificate() {
  const { state, update } = useProgress();
  const { user } = useUser();
  const done = doneCount(state);
  const name = state.m7.certName;

  useEffect(() => {
    if (!filled(name) && user?.fullName) update("m7", { certName: user.fullName });
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (done === 7 && !state.m7.certDate) update("m7", { certDate: new Date().toISOString() });
  }, [done]); // eslint-disable-line react-hooks/exhaustive-deps

  if (done < 7) return <NotYet done={done} />;
  const date = (state.m7.certDate ? new Date(state.m7.certDate) : new Date()).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="min-h-screen bg-[#EFE9E3] print:bg-white" data-testid="certificate-page">
      <div className="no-print sticky top-0 z-10 border-b border-line bg-ivory/95 px-5 py-3 backdrop-blur sm:px-8">
        <div className="mx-auto flex max-w-[1000px] flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Link to="/resultats" className="flex items-center gap-2 text-sm text-bx" data-testid="certificate-back"><ArrowLeft className="h-4 w-4" />Mes résultats</Link>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <label htmlFor="cert-name" className="text-sm text-soft">Nom sur le certificat</label>
            <input id="cert-name" value={name} onChange={(e) => update("m7", { certName: e.target.value.slice(0, 60) })} placeholder="Ton prénom et ton nom"
              className="rounded-xl border border-line bg-white px-4 py-2 text-base outline-none focus:border-bx focus:ring-2 focus:ring-bx/20" data-testid="certificate-name-input" />
            <Btn size="sm" onClick={() => window.print()} disabled={!filled(name)} data-testid="certificate-print-button"><Printer className="h-4 w-4" />Imprimer / PDF</Btn>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto px-4 py-8 print:p-0">
        <article className="certificate relative mx-auto aspect-[1.414/1] w-full min-w-[640px] max-w-[1000px] bg-white p-[5%] text-center shadow-[0_30px_70px_-35px_rgba(36,33,39,0.45)] print:min-w-0 print:shadow-none" data-testid="certificate">
          <div className="absolute inset-[3%] border border-gold/60" aria-hidden />
          <div className="absolute inset-[4%] border border-bx/20" aria-hidden />
          <Corner className="left-[3%] top-[3%] border-l-2 border-t-2" />
          <Corner className="right-[3%] top-[3%] border-r-2 border-t-2" />
          <Corner className="bottom-[3%] left-[3%] border-b-2 border-l-2" />
          <Corner className="bottom-[3%] right-[3%] border-b-2 border-r-2" />
          <div className="relative flex h-full flex-col items-center justify-between py-[2%]">
            <img src={PLATFORM.logoWordmark} alt={PLATFORM.name} className="h-[9%] w-auto" />
            <div>
              <p className="text-[clamp(0.6rem,1.3vw,0.85rem)] font-semibold uppercase tracking-[0.35em] text-gold-deep">Certificat de réussite</p>
              <p className="mt-[2%] text-[clamp(0.8rem,1.6vw,1.05rem)] text-soft">décerné à</p>
              <p className={`mt-[1.5%] font-display font-semibold italic leading-tight text-bx ${name.length > 32 ? "text-[clamp(1.4rem,3.2vw,2.4rem)]" : "text-[clamp(2rem,5vw,3.6rem)]"}`} data-testid="certificate-name">{filled(name) ? name : "Ton nom ici"}</p>
              <span className="mx-auto mt-[2%] block h-px w-1/3 bg-gold" aria-hidden />
              <p className="mx-auto mt-[3%] max-w-[70%] text-[clamp(0.8rem,1.6vw,1.05rem)] leading-relaxed text-ink/80">
                pour avoir terminé les 7 missions du parcours <strong className="text-bx">UGC GO — Ma première vidéo UGC</strong> : du choix du produit au script, du storyboard au tournage, jusqu'à l'auto-évaluation de sa création.
              </p>
            </div>
            <div className="flex w-[80%] items-end justify-between text-[clamp(0.65rem,1.3vw,0.85rem)] text-soft">
              <div className="text-left"><p className="font-semibold text-ink">{date}</p><p>Date</p></div>
              <img src={PLATFORM.logoIcon} alt="" className="h-[clamp(2.5rem,7vw,4.5rem)] w-auto" />
              <div className="text-right"><p className="font-semibold text-ink">{PLATFORM.name}</p><p>{PLATFORM.domain}</p></div>
            </div>
          </div>
        </article>
      </div>
      <p className="no-print pb-8 text-center text-xs text-soft">Astuce : choisis l'orientation « Paysage » dans la fenêtre d'impression.</p>
    </div>
  );
}
